import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { UserApi, RoleApi } from '../../services/ApiService';
// UPDATED: If you need AI services, import them directly
// import { AiDocumentProcessingApi } from '../../services/ai/AiDocumentProcessingApi';

function UserRolesPage() {
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('UserRolesPage: Loading data for user:', userId);
      
      // Load user details
      const userResponse = await UserApi.getUser(userId);
      if (!userResponse.ok) {
        throw new Error('Failed to load user details');
      }
      const userData = await userResponse.json();
      console.log('UserRolesPage: User data:', userData);
      setUser(userData);

      // Load user's current roles
      const userRolesResponse = await RoleApi.getUserRoles(userId);
      if (!userRolesResponse.ok) {
        throw new Error('Failed to load user roles');
      }
      const userRolesData = await userRolesResponse.json();
      console.log('UserRolesPage: User roles data:', userRolesData);
      setUserRoles(Array.isArray(userRolesData) ? userRolesData : []);

      // Load all available roles
      const allRolesResponse = await RoleApi.getRoles();
      if (!allRolesResponse.ok) {
        throw new Error('Failed to load available roles');
      }
      const allRolesData = await allRolesResponse.json();
      console.log('UserRolesPage: All roles data:', allRolesData);
      setAvailableRoles(Array.isArray(allRolesData) ? allRolesData : []);

    } catch (error) {
      console.error('UserRolesPage: Error loading data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAssignRole = async (roleId, roleName) => {
    try {
      console.log('UserRolesPage: Assigning role', roleId, 'to user', userId);
      
      const response = await RoleApi.assignRoleToUser(userId, roleId);
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to assign role: ${errorText}`);
      }

      setMessage(`Successfully assigned role "${roleName}" to user.`);
      loadData(); // Reload to get updated roles
    } catch (error) {
      console.error('UserRolesPage: Error assigning role:', error);
      setError(error.message);
    }
  };

  const handleRemoveRole = async (roleId, roleName) => {
    if (!window.confirm(`Are you sure you want to remove the role "${roleName}" from this user?`)) {
      return;
    }

    try {
      console.log('UserRolesPage: Removing role', roleId, 'from user', userId);
      
      const response = await RoleApi.removeRoleFromUser(userId, roleId);
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to remove role: ${errorText}`);
      }

      setMessage(`Successfully removed role "${roleName}" from user.`);
      loadData(); // Reload to get updated roles
    } catch (error) {
      console.error('UserRolesPage: Error removing role:', error);
      setError(error.message);
    }
  };

  const getRoleTypeColor = (roleName) => {
    const roleColors = {
      'admin': '#dc3545',
      'moderator': '#fd7e14',
      'instructor': '#6f42c1',
      'student': '#20c997',
      'user': '#6c757d',
      'manager': '#007bff'
    };
    
    const lowerRoleName = roleName?.toLowerCase();
    for (const [role, color] of Object.entries(roleColors)) {
      if (lowerRoleName?.includes(role)) {
        return color;
      }
    }
    return '#6c757d';
  };

  // Get roles that the user doesn't have yet
  const unassignedRoles = availableRoles.filter(role => 
    !userRoles.some(userRole => 
      (userRole.roleId || userRole.role_id) === (role.roleId || role.role_id)
    )
  );

  if (loading) {
    return (
      <div className="settings-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading user roles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page-content">
      <div className="settings-page-header">
        <div>
          <Link to="/settings/users" style={{ color: 'var(--primary-color)', textDecoration: 'none' }}>
            ← Back to Users
          </Link>
          <h2>Manage Roles for {user ? `${user.firstName || user.first_name || ''} ${user.lastName || user.last_name || ''}`.trim() || user.email : 'User'}</h2>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {message && (
        <div className="success-message" style={{ 
          padding: '1rem', 
          backgroundColor: 'var(--intent-success-bg, #d4edda)', 
          color: '#155724', 
          borderRadius: '4px', 
          marginBottom: '1rem',
          border: '1px solid #c3e6cb'
        }}>
          {message}
        </div>
      )}

      {user && (
        <div className="user-info-panel" style={{
          padding: '1rem',
          backgroundColor: 'var(--card-background)',
          borderRadius: '8px',
          marginBottom: '2rem',
          border: '1px solid var(--border-color)'
        }}>
          <h3>User Information</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            <div>
              <strong>Name:</strong> {`${user.firstName || user.first_name || ''} ${user.lastName || user.last_name || ''}`.trim() || 'N/A'}
            </div>
            <div>
              <strong>Email:</strong> {user.email}
            </div>
            <div>
              <strong>Status:</strong> 
              <span style={{ color: (user.isActive ?? user.is_active) !== false ? '#28a745' : '#dc3545', marginLeft: '0.5rem' }}>
                {(user.isActive ?? user.is_active) !== false ? 'Active' : 'Inactive'}
              </span>
            </div>
            {user.organization && (
              <div>
                <strong>Organization:</strong> {user.organization.name || user.organization.organizationName}
              </div>
            )}
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
        {/* Current Roles */}
        <div>
          <h3>Current Roles ({userRoles.length})</h3>
          {userRoles.length === 0 ? (
            <div className="empty-state">
              <p>No roles assigned to this user.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {userRoles.map((role, index) => (
                <div key={index} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0.75rem',
                  backgroundColor: 'var(--card-background)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span 
                      style={{
                        padding: '0.25rem 0.5rem',
                        backgroundColor: getRoleTypeColor(role.roleName || role.role_name),
                        color: 'white',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        textTransform: 'uppercase'
                      }}
                    >
                      {role.roleName || role.role_name}
                    </span>
                    {role.assignedAt && (
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                        Assigned: {new Date(role.assignedAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <button 
                    onClick={() => handleRemoveRole(role.roleId || role.role_id, role.roleName || role.role_name)}
                    className="btn-danger"
                    style={{
                      padding: '0.25rem 0.5rem',
                      fontSize: '0.75rem'
                    }}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Available Roles */}
        <div>
          <h3>Available Roles ({unassignedRoles.length})</h3>
          {unassignedRoles.length === 0 ? (
            <div className="empty-state">
              <p>All available roles have been assigned to this user.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {unassignedRoles.map((role) => (
                <div key={role.roleId} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0.75rem',
                  backgroundColor: 'var(--card-background)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span 
                      style={{
                        padding: '0.25rem 0.5rem',
                        backgroundColor: getRoleTypeColor(role.roleName),
                        color: 'white',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        textTransform: 'uppercase'
                      }}
                    >
                      {role.roleName}
                    </span>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      Created: {new Date(role.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <button 
                    onClick={() => handleAssignRole(role.roleId, role.roleName)}
                    className="btn-success"
                    style={{
                      padding: '0.25rem 0.5rem',
                      fontSize: '0.75rem'
                    }}
                  >
                    Assign
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="info-panel" style={{ marginTop: '2rem' }}>
        <h4>About User Roles</h4>
        <p>
          User roles define what permissions and access levels this user has within the system. 
          You can assign multiple roles to a user. Changes take effect immediately. Be careful when 
          removing admin roles, as this may prevent users from accessing administrative functions.
        </p>
      </div>
    </div>
  );
}

export default UserRolesPage;